/**
 * Lists pending and completed approvals for a DTA workflow stage.
 */

import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import StatusIndicator from "@cloudscape-design/components/status-indicator"
import React from "react"
import type { WorkflowApproval } from "@/generated/sdk/models/workflow-approval"
import { useApprovals, useSubmitApproval } from "@/hooks/queries/useWorkflow"

interface ApprovalListProps {
  dtaId: string
  stage?: string
  approvals?: WorkflowApproval[]
  onApprovalUpdate?: () => void
}

const ApprovalList: React.FC<ApprovalListProps> = ({
  dtaId,
  stage,
  approvals: propApprovals,
  onApprovalUpdate,
}) => {
  const { data, isPending, error } = useApprovals(dtaId, stage)
  const submitApproval = useSubmitApproval(dtaId, onApprovalUpdate)

  // Use prop approvals if provided (version-filtered from stream), otherwise fall back to API
  const approvalItems = propApprovals ?? data?.approvals ?? []

  if (isPending && !propApprovals) {
    return (
      <Box textAlign="center" padding="l">
        <Spinner /> Loading approvals...
      </Box>
    )
  }

  if (error) {
    return (
      <Box textAlign="center" padding="l">
        <StatusIndicator type="error">Error: {error.message}</StatusIndicator>
      </Box>
    )
  }

  const approvals = approvalItems

  const uniqueApprovals = approvals.filter(
    (approval, index, self) =>
      index === self.findIndex((a) => a.approval_id === approval.approval_id),
  )

  const pendingApprovals = uniqueApprovals.filter((a) => a.status === "PENDING")
  const completedApprovals = uniqueApprovals.filter(
    (a) => a.status === "APPROVED" || a.status === "REJECTED",
  )

  const deduplicatedCompleted = completedApprovals.reduce<WorkflowApproval[]>((acc, approval) => {
    const key = `${approval.approver_role}_${approval.stage}`
    const existing = acc.find((a) => `${a.approver_role}_${a.stage}` === key)
    if (!existing || new Date(approval.approved_at) > new Date(existing.approved_at)) {
      if (existing) {
        const index = acc.indexOf(existing)
        acc.splice(index, 1)
      }
      acc.push(approval)
    }
    return acc
  }, [] as WorkflowApproval[])

  return (
    <SpaceBetween size="l">
      {pendingApprovals.length > 0 && (
        <Container header={<Header variant="h3">Pending Approvals</Header>}>
          <SpaceBetween size="m">
            {pendingApprovals.map((approval) => (
              <SpaceBetween key={approval.approval_id} size="xs">
                <SpaceBetween size="xs" direction="horizontal">
                  <Box variant="strong">{approval.approver_role}</Box>
                  <Box color="text-status-inactive">{approval.stage}</Box>
                </SpaceBetween>
                <Button
                  variant="primary"
                  onClick={() => submitApproval.mutate(approval.stage)}
                  disabled={submitApproval.isPending}
                  loading={submitApproval.isPending && submitApproval.variables === approval.stage}
                >
                  {submitApproval.isPending && submitApproval.variables === approval.stage
                    ? "Submitting..."
                    : "Approve"}
                </Button>
              </SpaceBetween>
            ))}
          </SpaceBetween>
        </Container>
      )}

      {deduplicatedCompleted.length > 0 && (
        <Container header={<Header variant="h3">Completed Approvals</Header>}>
          <SpaceBetween size="m">
            {deduplicatedCompleted.map((approval) => (
              <SpaceBetween key={approval.approval_id} size="xs">
                <SpaceBetween size="xs" direction="horizontal">
                  <Box variant="strong">{approval.approver_role}</Box>
                  <Box color="text-status-inactive">{approval.stage}</Box>
                  <StatusIndicator type={approval.status === "APPROVED" ? "success" : "error"}>
                    {approval.status}
                  </StatusIndicator>
                </SpaceBetween>
                {approval.approval_comment && (
                  <Box color="text-body-secondary">{approval.approval_comment}</Box>
                )}
                {approval.rejection_reason && (
                  <Box color="text-status-error">{approval.rejection_reason}</Box>
                )}
                <Box color="text-status-inactive" fontSize="body-s">
                  {new Date(approval.approved_at).toLocaleString()} by {approval.approver_name}
                </Box>
              </SpaceBetween>
            ))}
          </SpaceBetween>
        </Container>
      )}

      {approvals.length === 0 && (
        <Box textAlign="center" padding="l" color="text-status-inactive">
          No approvals found
        </Box>
      )}
    </SpaceBetween>
  )
}

export default ApprovalList
